import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(
    "Notification" in window ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported("serviceWorker" in navigator && "PushManager" in window && "Notification" in window);
    setPermission("Notification" in window ? Notification.permission : "denied");
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    try {
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.publicKey) {
        console.warn("[Push] VAPID_PUBLIC_KEY não configurado no servidor.");
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
      return true;
    } catch (err) {
      console.error("[Push] Falha ao inscrever:", err);
      return false;
    }
  }, [supported]);

  const requestAndSubscribe = useCallback(async () => {
    if (!supported) return "unsupported";
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted") {
      await subscribe();
    }
    return result;
  }, [supported, subscribe]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error("[Push] Falha ao desinscrever:", err);
    }
  }, [supported]);

  // Verifica se já está inscrito ao carregar
  useEffect(() => {
    if (!supported || permission !== "granted") return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (sub) {
          setSubscribed(true);
          // Garantir que o backend tem essa subscription
          const subJson = sub.toJSON();
          api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [supported, permission]);

  return { supported, permission, subscribed, requestAndSubscribe, unsubscribe };
}
